/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createContext, FC, ReactNode, useContext } from "react";
import { UseLiveAPIResults, useLiveAPI } from "../hooks/use-live-api";
import { GeminiInterviewDetailsProps } from "../../../types/interviews";

// Define the context type explicitly to include interruptPlayback
// Ensure this matches the return type of useLiveAPI hook
type LiveAPIContextType = UseLiveAPIResults & { 
  interruptPlayback?: () => void; 
};

// Use the explicit type for createContext
const LiveAPIContext = createContext<LiveAPIContextType | undefined>(undefined);

export type LiveAPIProviderProps = {
  children: ReactNode;
  url?: string;
  apiKey: string;
  interviewDetails: GeminiInterviewDetailsProps;
  implementorUniqueId?: string;
};

export const LiveAPIProvider: FC<LiveAPIProviderProps> = ({
  url,
  apiKey,
  interviewDetails,
  implementorUniqueId,
  children,
}) => {
  // The value returned by useLiveAPI now includes interruptPlayback
  const liveAPI = useLiveAPI({ url, apiKey, interviewDetails, implementorUniqueId });

  // The liveAPI object passed as value now matches LiveAPIContextType
  return (
    <LiveAPIContext.Provider value={liveAPI}>
      {children}
    </LiveAPIContext.Provider>
  );
};

export const useLiveAPIContext = () => {
  const context = useContext(LiveAPIContext);
  if (!context) {
    throw new Error("useLiveAPIContext must be used wihin a LiveAPIProvider");
  }
  // The returned context now includes interruptPlayback
  return context;
};
